import {
  type CoinExpoAccountPositionValue, type CoinExpoAggregateAsset,
  type CoinExpoExposureAsset, type CoinExposureData,
} from '../../../models/exposure/exposure-types'
import { type ExposureDataGridRow } from '../../../pages/exposure/exposure-data-grid'

const DELIMITER = '&&'

const STABLE_COINS = ['USD', 'USDC', 'USDT']

function filterInPlace<T> (array: T[], condition: (value: T, index: number, array: T[]) => boolean, thisArg?: any): T[] {
  let newIndex = 0

  array.forEach((value, index) => {
    if (condition.call(thisArg, value, index, array)) {
      if (index !== newIndex) {
        array[newIndex] = value
      }
      newIndex++
    }
  })

  array.length = newIndex
  return array
}

export function prefilterCoinExposureData (coinExposureData: CoinExposureData): CoinExposureData {
  const copy: CoinExposureData = JSON.parse(JSON.stringify(coinExposureData))
  filterInPlace(copy.aggregate_asset, (value, index) => !STABLE_COINS.includes(value.coin))
  filterInPlace(copy.exposure_asset, (value, index) => !STABLE_COINS.includes(value.coin))
  filterInPlace(copy.account_positionvalue, (value, index) => !STABLE_COINS.includes(value.coin))
  return copy
}

export function extractForHeaderInitialExposure (coinExposureData: CoinExposureData): Record<string, string> {
  const results: Record<string, string> = {}
  const coinExpoAggregateAsset: CoinExpoAggregateAsset[] = coinExposureData.aggregate_asset

  if (coinExpoAggregateAsset != null) {
    coinExpoAggregateAsset.forEach(item => { results[item.coin] = item.init_amt })
  }

  return results
}

export function createCoinExposureRows (coinExposureData: CoinExposureData): ExposureDataGridRow[] {
  const coinExpoAggregateAsset: CoinExpoAggregateAsset[] = coinExposureData.aggregate_asset

  const coinRows: ExposureDataGridRow[] = coinExpoAggregateAsset.map(item => {
    return {
      id: item.coin,
      path: [item.coin],
      coin: item.coin,
      account: '',
      account_type: '',
      symbol: '',
      account_exposure: '',
      price_usd: item.price,
      exposure_coin: item.amount,
      exposure_usd: item.curr_price,
      initial_exposure_coin: item.init_amt,
      net_coin: item.difference,
      net_usd: item.diff_price,
    }
  })

  return coinRows
    .concat(createCoinAccountRows(coinExposureData))
    .concat(createCoinAccountSymbolRows(coinExposureData))
}

function createCoinAccountRows (coinExposureData: CoinExposureData): ExposureDataGridRow[] {
  const coinExpoExposureAsset: CoinExpoExposureAsset[] = coinExposureData.exposure_asset
  const coinExpoAccountPositionValue: CoinExpoAccountPositionValue[] = coinExposureData.account_positionvalue

  const uniqueOwnerAccountExposureSums: Record<string, number> = {}
  const uniqueOwnerExposureCoinSums: Record<string, number> = {}
  const uniqueOwnerExposureUsdSums: Record<string, number> = {}
  const uniqueOwnerInitialExposureCoinSums: Record<string, number> = {}
  const uniqueOwnerNetCoinSums: Record<string, number> = {}
  const uniqueOwnerNetUsdSums: Record<string, number> = {}

  for (let i = 0; i < coinExpoExposureAsset.length; i++) {
    const item = coinExpoExposureAsset[i]
    const { amount, coin, difference, owner } = item
    const currPrice = item.curr_price
    const diffPrice = item.diff_price
    const initAmt = item.init_amt
    const tag = `${String(coin)}${DELIMITER}${String(owner)}`

    if (Number.isFinite(uniqueOwnerAccountExposureSums[tag])) {
      uniqueOwnerAccountExposureSums[tag] += Number(amount)
      uniqueOwnerExposureCoinSums[tag] += Number(amount)
      uniqueOwnerExposureUsdSums[tag] += Number(currPrice)
      uniqueOwnerInitialExposureCoinSums[tag] += Number(initAmt)
      uniqueOwnerNetCoinSums[tag] += Number(difference)
      uniqueOwnerNetUsdSums[tag] += Number(diffPrice)
    } else {
      uniqueOwnerAccountExposureSums[tag] = Number(amount)
      uniqueOwnerExposureCoinSums[tag] = Number(amount)
      uniqueOwnerExposureUsdSums[tag] = Number(currPrice)
      uniqueOwnerInitialExposureCoinSums[tag] = Number(initAmt)
      uniqueOwnerNetCoinSums[tag] = Number(difference)
      uniqueOwnerNetUsdSums[tag] = Number(diffPrice)
    }
  }

  const ownerRows: ExposureDataGridRow[] = Object.keys(uniqueOwnerAccountExposureSums).map((key) => {
    const split = key.split(DELIMITER)
    const coin = split[0]
    const owner = split[1]

    return {
      id: `${coin}${DELIMITER}${owner}`,
      path: [coin, owner],
      coin: '',
      account: owner,
      account_type: '',
      symbol: '',
      account_exposure: uniqueOwnerAccountExposureSums[key].toString(),
      price_usd: '',
      exposure_coin: uniqueOwnerExposureCoinSums[key].toString(),
      exposure_usd: uniqueOwnerExposureUsdSums[key].toString(),
      initial_exposure_coin: uniqueOwnerInitialExposureCoinSums[key].toString(),
      net_coin: uniqueOwnerNetCoinSums[key].toString(),
      net_usd: uniqueOwnerNetUsdSums[key].toString(),
    }
  })

  const uniqueOwnerTypeAccountExposureSums: Record<string, number> = {}
  const uniqueOwnerTypeExposureCoinSums: Record<string, number> = {}
  const uniqueOwnerTypeExposureUsdSums: Record<string, number> = {}
  const uniqueOwnerTypeInitialExposureCoinSums: Record<string, number> = {}
  const uniqueOwnerTypeNetCoinSums: Record<string, number> = {}
  const uniqueOwnerTypeNetUsdSums: Record<string, number> = {}

  for (let i = 0; i < coinExpoExposureAsset.length; i++) {
    const item = coinExpoExposureAsset[i]
    const { amount, coin, difference, owner } = item
    const accountType = item.account_type
    const currPrice = item.curr_price
    const diffPrice = item.diff_price
    const initAmt = item.init_amt
    const tag = `${String(coin)}${DELIMITER}${String(owner)}${DELIMITER}${String(accountType)}`

    if (Number.isFinite(uniqueOwnerTypeAccountExposureSums[tag])) {
      uniqueOwnerTypeAccountExposureSums[tag] += Number(amount)
      uniqueOwnerTypeExposureCoinSums[tag] += Number(amount)
      uniqueOwnerTypeExposureUsdSums[tag] += Number(currPrice)
      uniqueOwnerTypeInitialExposureCoinSums[tag] += Number(initAmt)
      uniqueOwnerTypeNetCoinSums[tag] += Number(difference)
      uniqueOwnerTypeNetUsdSums[tag] += Number(diffPrice)
    } else {
      uniqueOwnerTypeAccountExposureSums[tag] = Number(amount)
      uniqueOwnerTypeExposureCoinSums[tag] = Number(amount)
      uniqueOwnerTypeExposureUsdSums[tag] = Number(currPrice)
      uniqueOwnerTypeInitialExposureCoinSums[tag] = Number(initAmt)
      uniqueOwnerTypeNetCoinSums[tag] = Number(difference)
      uniqueOwnerTypeNetUsdSums[tag] = Number(diffPrice)
    }
  }

  const uniqueOwnerTypePositionValues: Record<string, number> = {}

  for (let i = 0; i < coinExpoAccountPositionValue.length; i++) {
    const item = coinExpoAccountPositionValue[i]
    const { coin, owner } = item
    const accountType = item.account_type
    const positionValue = item.position_value
    const tag = `${String(coin)}${DELIMITER}${String(owner)}${DELIMITER}${String(accountType)}`

    uniqueOwnerTypePositionValues[tag] = Number(positionValue)
  }

  const ownerTypeRows: ExposureDataGridRow[] = Object.keys(uniqueOwnerTypeAccountExposureSums).map((key) => {
    const split = key.split(DELIMITER)
    const coin = split[0]
    const owner = split[1]
    const accountType = split[2]

    return {
      id: `${coin}${DELIMITER}${owner}${DELIMITER}${accountType}`,
      path: [coin, owner, accountType],
      coin: '',
      account: '',
      account_type: accountType,
      symbol: '',
      account_exposure: uniqueOwnerTypeAccountExposureSums[key].toString(),
      price_usd: '',
      exposure_coin: uniqueOwnerTypeExposureCoinSums[key].toString(),
      exposure_usd: uniqueOwnerTypeExposureUsdSums[key].toString(),
      initial_exposure_coin: uniqueOwnerTypeInitialExposureCoinSums[key].toString(),
      net_coin: uniqueOwnerTypeNetCoinSums[key].toString(),
      net_usd: uniqueOwnerTypeNetUsdSums[key].toString(),
      position_value: uniqueOwnerTypePositionValues[key]?.toString(),
    }
  })

  return ownerRows.concat(ownerTypeRows)
}

function createCoinAccountSymbolRows (coinExposureData: CoinExposureData): ExposureDataGridRow[] {
  const coinExpoExposureAsset: CoinExpoExposureAsset[] = coinExposureData.exposure_asset

  return coinExpoExposureAsset.map(item => {
    return {
      id: `${String(item.coin)}${DELIMITER}${String(item.owner)}${DELIMITER}${String(item.account_type)}${DELIMITER}${String(item.symbol_name)}`,
      path: [item.coin, item.owner, item.account_type, item.symbol_name],
      coin: '',
      account: '',
      account_type: '',
      symbol: item.symbol_name,
      account_exposure: item.amount,
      price_usd: '',
      exposure_coin: item.amount,
      exposure_usd: item.curr_price,
      initial_exposure_coin: item.init_amt,
      net_coin: item.difference,
      net_usd: item.diff_price,
    }
  })
}
